// src/pages/user/EquipmentDetails.jsx
import React, { useState } from 'react';
import { Container, Row, Col, Form, Modal } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  MdStar, 
  MdFavorite, 
  MdFavoriteBorder,
  MdCheckCircle,
  MdLocationOn,
  MdCalendarToday
} from 'react-icons/md';
import UserMasterLayout from '../../../layout/useradmin/UserMasterLayout';
import '../../../assets/css/useradmin/equipmentdetails/EquipmentDetails.css';

const EquipmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rentalDays, setRentalDays] = useState(1);

  // Mock equipment data
  const equipment = {
    id: 1,
    name: 'John Deere 5050D Tractor',
    category: 'Tractors',
    price: 3500,
    priceUnit: 'day',
    rating: 4.5,
    totalReviews: 28,
    availability: 'available',
    location: 'Gujranwala, Punjab',
    owner: 'Ahmed Farm Equipment',
    ownerContact: '+92 300 1234567',
    description: 'Heavy-duty tractor ideal for large-scale farming operations. Features include 4-wheel drive, power steering, and comfortable cabin. Perfect for plowing, tilling, and heavy lifting tasks.',
    specifications: [
      { label: 'Engine Power', value: '50 HP' },
      { label: 'Fuel Type', value: 'Diesel' },
      { label: 'Year', value: '2022' },
      { label: 'Condition', value: 'Excellent' }
    ],
    features: [
      'Power Steering',
      '4-Wheel Drive',
      'Air Conditioned Cabin',
      'Hydraulic Lift',
      'PTO (Power Take-Off)',
      'LED Lights'
    ],
    images: ['🚜', '🔧', '⚙️']
  };

  const reviews = [
    {
      id: 1,
      userName: 'Muhammad Hassan',
      rating: 5,
      date: '2 weeks ago',
      comment: 'Excellent tractor! Very powerful and fuel-efficient. Owner was very cooperative.'
    },
    {
      id: 2,
      userName: 'Ali Raza',
      rating: 4,
      date: '1 month ago',
      comment: 'Good equipment. Works perfectly for heavy-duty tasks. Highly recommended.'
    },
    {
      id: 3,
      userName: 'Fatima Khan',
      rating: 5,
      date: '2 months ago',
      comment: 'Amazing service! The tractor was in great condition and delivery was on time.'
    }
  ];

  const calculateTotal = () => {
    return equipment.price * rentalDays;
  };

  const handleBooking = () => {
    setShowBookingModal(true);
  };

  const confirmBooking = () => {
    // Add booking logic here
    alert('Booking request sent successfully!');
    setShowBookingModal(false);
    navigate('/user/my-rentals');
  };

  return (
    <UserMasterLayout>
      <Container fluid className="equipment-details-page">
        
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <span onClick={() => navigate('/user/browse-equipment')}>Browse Equipment</span>
          <span className="separator">/</span>
          <span className="current">{equipment.name}</span>
        </div>

        <Row>
          {/* Left Column - Images & Details */}
          <Col lg={8} md={12}>
            
            {/* Image Gallery */}
            <div className="image-gallery">
              <div className="main-image">
                <span className="equipment-main-emoji">{equipment.images[0]}</span>
                <button 
                  className="favorite-btn-large"
                  onClick={() => setIsFavorite(!isFavorite)}
                >
                  {isFavorite ? (
                    <MdFavorite style={{ color: '#FDB714' }} />
                  ) : (
                    <MdFavoriteBorder />
                  )}
                </button>
              </div>
              <div className="thumbnail-images">
                {equipment.images.map((img, index) => (
                  <div key={index} className="thumbnail">
                    <span>{img}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Equipment Info */}
            <div className="details-section">
              <div className="title-section">
                <h1>{equipment.name}</h1>
                <div className="rating-badge">
                  <MdStar style={{ color: '#FDB714' }} />
                  <span>{equipment.rating}</span>
                  <span className="review-count">({equipment.totalReviews} reviews)</span>
                </div>
              </div>

              <div className="meta-info">
                <span className="category-badge">{equipment.category}</span>
                <span className={`status-badge ${equipment.availability}`}>
                  <MdCheckCircle /> {equipment.availability === 'available' ? 'Available' : 'Rented'}
                </span>
              </div>

              {/* Description */}
              <div className="description-section">
                <h3>Description</h3>
                <p>{equipment.description}</p>
              </div>

              {/* Specifications */}
              <div className="specifications-section">
                <h3>Specifications</h3>
                <div className="specs-grid">
                  {equipment.specifications.map((spec, index) => (
                    <div key={index} className="spec-item">
                      <span className="spec-label">{spec.label}:</span>
                      <span className="spec-value">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div className="features-section">
                <h3>Features</h3>
                <div className="features-grid">
                  {equipment.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <MdCheckCircle style={{ color: '#28a745' }} />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Owner Info */}
              <div className="owner-section">
                <h3>Owner Information</h3>
                <div className="owner-card">
                  <div className="owner-avatar">A</div>
                  <div className="owner-info">
                    <h4>{equipment.owner}</h4>
                    <p><MdLocationOn /> {equipment.location}</p>
                    <p>📞 {equipment.ownerContact}</p>
                  </div>
                  <button className="contact-owner-btn">Contact Owner</button>
                </div>
              </div>

              {/* Reviews */}
              <div className="reviews-section">
                <h3>Customer Reviews ({equipment.totalReviews})</h3>
                <div className="reviews-list">
                  {reviews.map((review) => (
                    <div key={review.id} className="review-item">
                      <div className="review-header">
                        <div className="reviewer-info">
                          <div className="reviewer-avatar">
                            {review.userName.charAt(0)}
                          </div>
                          <div>
                            <h5>{review.userName}</h5>
                            <span className="review-date">{review.date}</span>
                          </div>
                        </div>
                        <div className="review-rating">
                          {[...Array(5)].map((_, i) => (
                            <MdStar 
                              key={i}
                              style={{ 
                                color: i < review.rating ? '#FDB714' : '#e0e0e0' 
                              }}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="review-comment">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </Col>

          {/* Right Column - Booking Card */}
          <Col lg={4} md={12}>
            <div className="booking-card sticky-card">
              <div className="price-section">
                <span className="price">Rs {equipment.price.toLocaleString()}</span>
                <span className="price-unit">/ {equipment.priceUnit}</span>
              </div>

              <Form className="booking-form">
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control 
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control 
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Number of Days</Form.Label>
                  <Form.Control 
                    type="number"
                    min="1"
                    value={rentalDays}
                    onChange={(e) => setRentalDays(parseInt(e.target.value) || 1)}
                  />
                </Form.Group>

                <div className="price-breakdown">
                  <div className="breakdown-row">
                    <span>Rs {equipment.price} × {rentalDays} days</span>
                    <span>Rs {calculateTotal().toLocaleString()}</span>
                  </div>
                  <div className="breakdown-row total">
                    <span>Total</span>
                    <span>Rs {calculateTotal().toLocaleString()}</span>
                  </div>
                </div>

                <button 
                  type="button"
                  className="rent-now-btn-large"
                  onClick={handleBooking}
                  disabled={equipment.availability !== 'available'}
                >
                  {equipment.availability === 'available' ? 'Book Now' : 'Currently Unavailable'}
                </button>
              </Form>

              <div className="booking-note">
                <p>💡 Free cancellation up to 24 hours before rental</p>
                <p>✅ Insurance included in price</p>
              </div>
            </div>
          </Col>
        </Row>

        {/* Booking Confirmation Modal */}
        <Modal show={showBookingModal} onHide={() => setShowBookingModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Booking</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="booking-summary">
              <h5>{equipment.name}</h5>
              <p>Duration: {rentalDays} days</p>
              <p>Total Amount: <strong>Rs {calculateTotal().toLocaleString()}</strong></p>
              <p className="note">A booking request will be sent to the owner</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="cancel-modal-btn" onClick={() => setShowBookingModal(false)}>
              Cancel
            </button>
            <button className="confirm-modal-btn" onClick={confirmBooking}>
              Confirm Booking
            </button>
          </Modal.Footer>
        </Modal>

      </Container>
    </UserMasterLayout>
  );
};

export default EquipmentDetails;